import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Card,
  CardContent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  fetchScrapers,
  deleteScraper,
  fetchStats,
} from "@/store/scrapersSlice";
import * as scrapersApi from "@api/scrapers.api";
import { useNotification } from "@context/NotificationContext";
import { extractErrorMessage } from "@api/axios";
import { ConfirmDialog } from "@components/ConfirmDialog";
import { PageHeader } from "@components/PageHeader";
import { ScraperStatusChip } from "@components/ScraperStatusChip";
import { useScrapersBootstrap } from "@hooks/useScrapersBootstrap";

export default function Scrapers() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  useScrapersBootstrap();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const scrapers = useAppSelector((s) => s.scrapers.list);
  const stats = useAppSelector((s) => s.scrapers.stats);
  const cronJobs = useAppSelector((s) => s.cronJobs.list);
  const loading = useAppSelector((s) => s.scrapers.loading);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(deleteScraper(deleteTarget)).unwrap();
      dispatch(fetchStats());
      notify(t("scrapers.scraperDeleted"), "success");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
    setDeleteTarget(null);
  };

  const handleRun = async (id: string) => {
    try {
      await scrapersApi.runScraper(id);
      notify(t("scrapers.executionSuccess"), "success");
    } catch (err) {
      notify(extractErrorMessage(err), "error");
    }
  };

  const getCronExpression = (scraperId: string) => {
    const job = cronJobs.find((j) => j.scraperId === scraperId);
    return job?.expression ?? "-";
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <PageHeader sx={{ mb: 0 }}>{t("scrapers.title")}</PageHeader>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/scrapers/new")}
          disabled={stats ? stats.totalScrapers >= stats.maxScrapers : false}
        >
          {t("scrapers.newScraper")}
        </Button>
      </Box>
      <Card>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("scrapers.name")}</TableCell>
                  <TableCell>{t("scrapers.targetUrl")}</TableCell>
                  <TableCell>{t("scrapers.schedule")}</TableCell>
                  <TableCell>{t("scrapers.status")}</TableCell>
                  <TableCell align="right">{t("scrapers.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scrapers.map((scraper) => (
                  <TableRow key={scraper.id} hover>
                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {scraper.name}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {scraper.base}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                        }}
                      >
                        {getCronExpression(scraper.id)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ScraperStatusChip
                        status={scraper.status}
                        label={t(`scrapers.${scraper.status}`)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={t("scrapers.run")}>
                        <IconButton
                          size="small"
                          onClick={() => handleRun(scraper.id)}
                        >
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("scrapers.edit")}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(`/scrapers/${scraper.id}/edit`)
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t("scrapers.delete")}>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteTarget(scraper.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && scrapers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography
                        sx={{
                          color: "text.secondary",
                          py: 4,
                        }}
                      >
                        {t("scrapers.emptyList")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={!!deleteTarget}
        title={t("scrapers.delete")}
        message={t("scrapers.confirmDelete")}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
